"use client";

import React, { useEffect, useRef, useState } from "react";
import { getVideoPosterUrl } from "@/lib/video";

interface LazyVideoProps {
  url: string;
  /** Optional explicit poster. Falls back to the Cloudflare Stream thumbnail. */
  poster?: string;
  /**
   * `loading` for the poster <img>. Pass "lazy" for below-the-fold tiles —
   * React 19 SSR preloads every non-lazy image it renders.
   */
  posterLoading?: "eager" | "lazy";
  width?: string | number;
  height?: string | number;
  className?: string;
  style?: React.CSSProperties;
  /** Show native playback controls when reduced motion prevents autoplay. */
  reducedMotionControls?: boolean;
  ariaLabel?: string;
  /** How far outside the viewport a preview may begin loading. */
  viewportMargin?: string;
}

/**
 * Muted, looping autoplay video that only attaches hls.js while it is in (or
 * near) the viewport. A poster image is shown until the first frame is ready,
 * and the player is torn down again when scrolled away.
 *
 * Uses a native `<video>` element driven directly by hls.js (or the browser's
 * native HLS in Safari) rather than react-player — this avoids react-player's
 * unreliable `muted` reflection and CDN-loaded hls.js timing, which left
 * Cloudflare Stream videos stuck on a blank/poster frame.
 *
 * Lazy mounting also caps the number of simultaneous MediaSource/decoder-backed
 * `<video>` elements; rendering many at once exhausts the browser's media
 * pipeline (surfacing as `NotSupportedError` / `hlsError`).
 */
export default function LazyVideo({
  url,
  poster,
  posterLoading,
  width = "100%",
  height = "100%",
  className,
  style,
  reducedMotionControls = true,
  ariaLabel = "Video",
  viewportMargin = "200px",
}: LazyVideoProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [inView, setInView] = useState(false);
  const [sourceReady, setSourceReady] = useState(false);
  const [ready, setReady] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  const posterUrl = poster ?? getVideoPosterUrl(url) ?? undefined;

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updatePreference = () => setPrefersReducedMotion(mediaQuery.matches);

    updatePreference();
    mediaQuery.addEventListener("change", updatePreference);
    return () => mediaQuery.removeEventListener("change", updatePreference);
  }, []);

  // Observe viewport visibility so we only attach hls.js when needed.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { rootMargin: viewportMargin },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [viewportMargin]);

  // Attach/detach the media source based on visibility.
  useEffect(() => {
    const video = videoRef.current;
    if (!inView || !video) return;

    let cancelled = false;
    let hls: import("hls.js").default | null = null;

    const onSourceReady = () => {
      if (cancelled) return;
      setSourceReady(true);
      video.muted = true;
    };

    const onFirstFrame = () => {
      if (!cancelled) setReady(true);
    };

    video.addEventListener("loadeddata", onFirstFrame);
    video.addEventListener("playing", onFirstFrame);

    const playNative = () => {
      video.src = url;
      onSourceReady();
    };

    // Prefer hls.js whenever it's supported (Chrome, Firefox, Edge, and
    // Chromium-based embedded browsers). Some of those browsers incorrectly
    // report native HLS support via canPlayType but cannot actually decode a
    // raw .m3u8, so we only fall back to native playback when hls.js is NOT
    // supported (genuine Safari / iOS). hls.js is dynamically imported so it
    // stays out of the initial bundle.
    void import("hls.js").then(({ default: Hls }) => {
      if (cancelled) return;

      if (!Hls.isSupported()) {
        // Safari / iOS: native HLS.
        playNative();
        return;
      }

      // These tiles are small, so cap the variant to the player size and start
      // at the lowest quality for the fastest possible first frame.
      hls = new Hls({
        enableWorker: true,
        capLevelToPlayerSize: true,
        startLevel: 0,
        maxBufferLength: 10,
      });
      hls.loadSource(url);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, onSourceReady);
      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (!data.fatal || !hls) return;
        if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
          hls.startLoad();
        } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
          hls.recoverMediaError();
        } else {
          hls.destroy();
          hls = null;
        }
      });
    });

    return () => {
      cancelled = true;
      video.removeEventListener("loadeddata", onFirstFrame);
      video.removeEventListener("playing", onFirstFrame);
      if (hls) {
        hls.destroy();
        hls = null;
      }
      video.removeAttribute("src");
      video.load();
      setSourceReady(false);
      setReady(false);
    };
  }, [inView, url]);

  // Keep autoplay in sync with both viewport visibility and the user's motion
  // preference. Reduced-motion users can opt in with native controls instead.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !sourceReady || !inView) return;

    if (prefersReducedMotion) {
      video.pause();
      return;
    }

    void video.play().catch(() => {});
  }, [inView, prefersReducedMotion, sourceReady]);

  const showControls = prefersReducedMotion && reducedMotionControls;

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        position: "relative",
        width,
        height,
        backgroundColor: "#f0f0f0",
        ...style,
      }}
    >
      {posterUrl && (
        <img
          src={posterUrl}
          alt=""
          aria-hidden
          loading={posterLoading}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            opacity: ready || showControls ? 0 : 1,
            transition: prefersReducedMotion ? "none" : "opacity 0.3s ease",
          }}
        />
      )}
      <video
        ref={videoRef}
        muted
        loop
        playsInline
        preload="none"
        poster={showControls ? posterUrl : undefined}
        controls={showControls}
        aria-label={showControls ? ariaLabel : undefined}
        aria-hidden={showControls ? undefined : true}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
        }}
      />
    </div>
  );
}
